import React from "react";
import { FileQuestion } from "lucide-react";

/**
 * Reusable empty-state block for list/grid surfaces.
 *
 * Drop-in replacement for the 3 inline implementations previously living
 * in Alerts.jsx, VigilanceReports.jsx and UnderWatch.jsx. Centralises the
 * visual treatment (dashed border, 12-icon, 3-line vertical rhythm) so
 * future polish only happens in one place.
 *
 * Props:
 *   - icon:        lucide-react component (default FileQuestion)
 *   - title:       short headline ("Aucune alerte critique")
 *   - subtitle:    one-sentence context ("Vos contrats sont en règle.
 *                  Continuez à uploader pour étendre la surveillance.")
 *   - action:      { label, onClick, loading?, disabled?, testId? }
 *                  Optional CTA. When omitted, no button is rendered.
 *   - variant:     "default" (white card) | "subtle" (transparent — for
 *                  in-grid placements)
 *   - testId:      data-testid root; child elements derive from it.
 *
 * Examples in /app/frontend/src/pages/{Alerts,VigilanceReports,UnderWatch}.jsx.
 */
export default function EmptyState({
  icon: Icon = FileQuestion,
  title,
  subtitle,
  action,
  variant = "default",
  className = "",
  testId = "empty-state",
}) {
  const wrapClass =
    variant === "subtle"
      ? "text-center py-12 px-6"
      : "text-center py-12 px-6 rounded-2xl border border-dashed border-zinc-300 bg-white";

  return (
    <div
      className={`${wrapClass} ${className}`.trim()}
      data-testid={testId}
    >
      <span
        className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-zinc-100 text-zinc-400 mb-3"
        data-testid={`${testId}-icon`}
      >
        <Icon size={20} strokeWidth={1.5} />
      </span>
      {title && (
        <h3
          className="font-display text-base font-semibold text-qenva-navy"
          data-testid={`${testId}-title`}
        >
          {title}
        </h3>
      )}
      {subtitle && (
        <p
          className="text-[13.5px] text-zinc-500 max-w-md mx-auto mt-1.5"
          data-testid={`${testId}-subtitle`}
        >
          {subtitle}
        </p>
      )}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          disabled={action.disabled || action.loading}
          data-testid={action.testId || `${testId}-action`}
          className="inline-flex items-center gap-1.5 mt-5 px-4 py-2 rounded-lg bg-qenva-blue text-white text-[13px] font-semibold hover:bg-qenva-blue/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {action.icon && <action.icon size={14} strokeWidth={2} />}
          {action.loading ? action.loadingLabel || action.label : action.label}
        </button>
      )}
    </div>
  );
}
